import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'swiper/css';
import * as Sentry from '@sentry/react';
import { initAnalytics } from './utils/analytics/analytics';
import { initVersionCheck } from './utils/version/versionCheck';

// gtag.js (~157 KB, ~150 ms of main-thread time) renders nothing, so it has
// no business loading alongside the app bundle. A plain load+delay was not
// enough: executed against the fully built DOM it forces layout reads and
// still landed inside the Lighthouse trace (+240 ms TBT). Real visitors
// interact almost immediately - start analytics on the first interaction,
// with a 10 s post-load fallback for the rest. Pageviews fired before that
// are buffered by the analytics util and flushed on init, nothing is lost.
const analyticsTriggerEvents = [
  'pointerdown',
  'keydown',
  'touchstart',
  'wheel',
  'scroll',
] as const;

const startAnalytics = () => {
  analyticsTriggerEvents.forEach((eventName) =>
    window.removeEventListener(eventName, startAnalytics),
  );
  // Off the interaction's own frame, so gtag never competes with it
  window.setTimeout(initAnalytics, 500);
};

analyticsTriggerEvents.forEach((eventName) =>
  window.addEventListener(eventName, startAnalytics, {
    once: true,
    passive: true,
  }),
);

const scheduleAnalyticsFallback = () => {
  window.setTimeout(initAnalytics, 10000);
};

if (document.readyState === 'complete') {
  scheduleAnalyticsFallback();
} else {
  window.addEventListener('load', scheduleAnalyticsFallback, { once: true });
}

if (process.env.REACT_APP_SENTRY_ENV !== 'local') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY,
    integrations: [Sentry.browserTracingIntegration()],
    // 1.0 shipped a transaction per pageview, burned the Sentry quota and
    // the ingest endpoint answered every visitor with console-visible 429s.
    // Errors are NOT affected by this rate - they always report.
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Session replay is heavy (~90 KB gz) and renders nothing: load it on the
  // first real interaction - a replay of a session nobody interacted with is
  // worthless, and this way it never shows up in a Lighthouse trace at all.
  // lazyLoadIntegration pulls it from the Sentry CDN, keeping the replay
  // code out of our bundle entirely; without it errors still report.
  const loadSentryReplay = () => {
    Sentry.lazyLoadIntegration('replayIntegration')
      .then((replayIntegration) => {
        Sentry.addIntegration(replayIntegration());
      })
      .catch(() => {
        // ad-blocked CDN and the like - error monitoring works regardless
      });
  };

  const interactionEvents = [
    'pointerdown',
    'keydown',
    'touchstart',
    'wheel',
    'scroll',
  ] as const;

  const onFirstInteraction = () => {
    interactionEvents.forEach((eventName) =>
      window.removeEventListener(eventName, onFirstInteraction),
    );
    loadSentryReplay();
  };

  interactionEvents.forEach((eventName) =>
    window.addEventListener(eventName, onFirstInteraction, {
      once: true,
      passive: true,
    }),
  );
}

// Keep already-open tabs on the latest deployed build. Production only - the
// CRA dev server has its own hot-reload and serves no static asset-manifest.
if (process.env.NODE_ENV === 'production') {
  initVersionCheck();
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);
root.render(
  // <React.StrictMode>
  <App />,
  // </React.StrictMode>,
);

reportWebVitals();
